class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 250);
    this.debouncedPriceSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm?.addEventListener("input", (event) => {
      if (FacetFiltersForm.isSortControl(event.target)) {
        this.onSubmitHandler(event);
      } else if (FacetFiltersForm.isPriceControl(event.target)) {
        this.debouncedPriceSubmit(event);
      } else {
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false, "history");
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static isSortControl(target) {
    return Boolean(target?.matches?.('[data-role="facet-sort-select"], [name="sort_by"]'));
  }

  static isPriceControl(target) {
    return Boolean(
      target?.closest?.("price-range") ||
        target?.name?.startsWith?.("filter.v.price") ||
        target?.name?.startsWith?.("filter.p.price")
    );
  }

  static getOperation(event, operationOverride) {
    if (operationOverride) return operationOverride;
    if (FacetFiltersForm.isSortControl(event?.target)) return "sort";
    if (FacetFiltersForm.isPriceControl(event?.target)) return "price";
    return "filter";
  }

  static getNow() {
    return typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  }

  static isAbortError(error) {
    return error?.name === "AbortError" || error?.code === 20;
  }

  static throwIfAborted(signal) {
    if (!signal.aborted) return;
    const abortError = new Error("Facet request was aborted");
    abortError.name = "AbortError";
    throw abortError;
  }

  static getResultCountBucket() {
    const resultCount = document.querySelectorAll("#ProductGridContainer .grid__item").length;
    if (resultCount === 0) return "0";
    if (resultCount <= 8) return "1-8";
    if (resultCount <= 24) return "9-24";
    return "25+";
  }

  static getNormalizedPathname() {
    const pathname = window.location?.pathname || "/";
    return pathname.replace(/\/+$/, "") || "/";
  }

  static isSearchPage() {
    return FacetFiltersForm.getNormalizedPathname() === "/search";
  }

  static isCollectionPage() {
    return FacetFiltersForm.getNormalizedPathname().startsWith("/collections/");
  }

  // Search timing is sampled per page view: unsampled it was ~22k events a day.
  // Shared with predictive-search.js through window, so a page is in or out for both.
  static isInSearchTimingSample() {
    if (typeof window.__plugSearchTimingSampled !== "boolean") {
      window.__plugSearchTimingSampled = Math.random() < 0.1;
    }
    return window.__plugSearchTimingSampled;
  }

  static captureTiming({ operation, startedAt, outcome, aborted = false, cacheHit = false }) {
    const isSearchPage = FacetFiltersForm.isSearchPage();
    const isCollectionPage = FacetFiltersForm.isCollectionPage();
    if (!isSearchPage && !isCollectionPage) return;
    if (isSearchPage && !FacetFiltersForm.isInSearchTimingSample()) return;

    const pathname = FacetFiltersForm.getNormalizedPathname();
    const origin = window.location?.origin || "";
    const eventName = isSearchPage ? "storefront_search_timing" : "storefront_collection_timing";
    window.posthog?.capture?.(eventName, {
      surface: "facets",
      operation,
      duration_ms: Math.max(0, Math.round(FacetFiltersForm.getNow() - startedAt)),
      outcome,
      aborted,
      cache_hit: cacheHit,
      result_count_bucket: FacetFiltersForm.getResultCountBucket(),
      $current_url: `${origin}${pathname}`,
      ...(isSearchPage && { sample_rate: 0.1 }),
      ...(isCollectionPage && {
        plug_perf_asset_release: "compact-collections-v1-20260817",
      }),
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true, operationOverride = null) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const operation = FacetFiltersForm.getOperation(event, operationOverride);
    const startedAt = FacetFiltersForm.getNow();
    const requestId = ++FacetFiltersForm.requestId;

    FacetFiltersForm.abortController?.abort();
    const abortController = new AbortController();
    FacetFiltersForm.abortController = abortController;

    const sectionRequests = sections.map((section) => {
      const separator = searchParams ? "&" : "";
      const url = `${window.location.pathname}?section_id=${section.section}${separator}${searchParams}`;
      const cachedSection = FacetFiltersForm.filterData.find(
        (filterData) => filterData.url === url
      );
      return {
        url,
        cachedSection,
      };
    });
    const cacheHit = sectionRequests.every(({ cachedSection }) => Boolean(cachedSection));

    FacetFiltersForm.setLoadingState(true);

    try {
      const sectionDocuments = await Promise.all(
        sectionRequests.map(({ url, cachedSection }) =>
          cachedSection
            ? Promise.resolve(cachedSection.document)
            : FacetFiltersForm.fetchSectionDocument(url, abortController.signal)
        )
      );

      if (requestId !== FacetFiltersForm.requestId || abortController.signal.aborted) {
        FacetFiltersForm.captureTiming({
          operation,
          startedAt,
          outcome: "superseded",
          aborted: true,
          cacheHit,
        });
        return;
      }

      sectionDocuments.forEach((sectionDocument) => {
        FacetFiltersForm.renderSectionDocument(sectionDocument, event);
      });

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      document.dispatchEvent(new CustomEvent("collection:product-grid-rendered"));
      FacetFiltersForm.clearFailureAnnouncement();
      FacetFiltersForm.captureTiming({ operation, startedAt, outcome: "success", cacheHit });
    } catch (error) {
      const aborted = FacetFiltersForm.isAbortError(error) || abortController.signal.aborted;
      const superseded = requestId !== FacetFiltersForm.requestId;
      FacetFiltersForm.captureTiming({
        operation,
        startedAt,
        outcome: superseded ? "superseded" : aborted ? "aborted" : "error",
        aborted,
        cacheHit,
      });

      if (!aborted && requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.announceFailure();
      }
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoadingState(false);
        if (FacetFiltersForm.abortController === abortController)
          FacetFiltersForm.abortController = null;
      }
    }
  }

  static async fetchSectionDocument(url, signal) {
    const response = await fetch(url, { signal });
    FacetFiltersForm.throwIfAborted(signal);
    if (!response.ok) throw new Error(`Facet request failed with status ${response.status}`);

    const responseText = await response.text();
    FacetFiltersForm.throwIfAborted(signal);
    const sectionDocument = new DOMParser().parseFromString(responseText, "text/html");
    if (!sectionDocument.getElementById("ProductGridContainer")) {
      throw new Error("Facet response did not contain a product grid");
    }

    FacetFiltersForm.filterData = [
      ...FacetFiltersForm.filterData,
      { document: sectionDocument, url },
    ];
    return sectionDocument;
  }

  static renderSectionDocument(sectionDocument, event) {
    FacetFiltersForm.renderFilters(sectionDocument, event);
    FacetFiltersForm.renderProductGridContainer(sectionDocument);
    FacetFiltersForm.renderProductCount(sectionDocument);
    if (typeof initializeScrollAnimationTrigger === "function") {
      const renderedGrid = document.getElementById("ProductGridContainer");
      if (renderedGrid) initializeScrollAnimationTrigger(renderedGrid);
    }
  }

  static setLoadingState(isLoading) {
    const gridContainer = document.getElementById("ProductGridContainer");
    const collection = gridContainer?.querySelector(".collection");
    const countContainers = [
      document.getElementById("ProductCount"),
      document.getElementById("ProductCountDesktop"),
    ];
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );

    gridContainer?.setAttribute("aria-busy", String(isLoading));
    document.querySelectorAll("facet-filters-form form").forEach((form) => {
      form.setAttribute("aria-busy", String(isLoading));
    });
    collection?.classList.toggle("loading", isLoading);
    countContainers.forEach((container) => container?.classList.toggle("loading", isLoading));
    loadingSpinners.forEach((spinner) => spinner.classList.toggle("hidden", !isLoading));
    FacetFiltersForm.toggleActiveFacets(isLoading);

    if (isLoading && gridContainer && !FacetFiltersForm.gridHeightState) {
      FacetFiltersForm.gridHeightState = {
        container: gridContainer,
        minHeight: gridContainer.style.minHeight,
      };
      const currentHeight = Math.ceil(gridContainer.getBoundingClientRect().height);
      if (currentHeight > 0) gridContainer.style.minHeight = `${currentHeight}px`;
    } else if (!isLoading && FacetFiltersForm.gridHeightState) {
      const { container, minHeight } = FacetFiltersForm.gridHeightState;
      container.style.minHeight = minHeight;
      FacetFiltersForm.gridHeightState = null;
    }
  }

  static getFailureStatusElement() {
    let statusElement = document.getElementById("FacetResultsStatus");
    if (statusElement) return statusElement;

    statusElement = document.createElement("div");
    statusElement.id = "FacetResultsStatus";
    statusElement.className = "visually-hidden";
    statusElement.setAttribute("role", "status");
    statusElement.setAttribute("aria-live", "polite");
    document.getElementById("ProductGridContainer")?.before(statusElement);
    return statusElement;
  }

  static announceFailure() {
    const statusElement = FacetFiltersForm.getFailureStatusElement();
    statusElement.textContent = "We couldn't update the results. Please try again.";
  }

  static clearFailureAnnouncement() {
    const statusElement = document.getElementById("FacetResultsStatus");
    if (statusElement) statusElement.textContent = "";
  }

  static hydrateMobileFacetOptions(root = document, preserveMobileSelection = false) {
    const desktopInputs = Array.from(
      root.querySelectorAll('#FacetFiltersForm input[type="checkbox"]')
    );
    const mobileInputs = Array.from(
      root.querySelectorAll('#FacetFiltersFormMobile input[type="checkbox"]')
    );
    if (desktopInputs.length === 0 || mobileInputs.length === 0) return;

    const desktopInputsByName = new Map();
    desktopInputs.forEach((input) => {
      const inputs = desktopInputsByName.get(input.name) || [];
      inputs.push(input);
      desktopInputsByName.set(input.name, inputs);
    });

    const mobileIndexesByName = new Map();
    mobileInputs.forEach((mobileInput) => {
      const index = mobileIndexesByName.get(mobileInput.name) || 0;
      mobileIndexesByName.set(mobileInput.name, index + 1);
      const desktopInput = desktopInputsByName.get(mobileInput.name)?.[index];
      if (!desktopInput) return;

      mobileInput.value = desktopInput.value;
      mobileInput.setAttribute?.("value", desktopInput.value);
      if (!preserveMobileSelection) mobileInput.checked = desktopInput.checked;
      mobileInput.disabled = desktopInput.disabled;

      const desktopLabel = desktopInput.labels?.[0] || desktopInput.closest?.("label");
      const mobileLabel = mobileInput.labels?.[0] || mobileInput.closest?.("label");
      if (!desktopLabel || !mobileLabel) return;

      mobileLabel.classList?.toggle("active", desktopInput.checked);
      mobileLabel.classList?.toggle("disabled", desktopInput.disabled);

      const desktopTextNodes = desktopLabel.querySelectorAll?.(".facet-checkbox__text") || [];
      const mobileTextNodes = mobileLabel.querySelectorAll?.(".facet-checkbox__text") || [];
      desktopTextNodes.forEach((desktopTextNode, textIndex) => {
        if (mobileTextNodes[textIndex]) {
          mobileTextNodes[textIndex].innerHTML = desktopTextNode.innerHTML;
        }
      });

      if (desktopLabel.classList?.contains("swatch-input__label")) {
        mobileLabel.title = desktopLabel.title;
        mobileLabel.className = desktopLabel.className;
        mobileLabel.innerHTML = desktopLabel.innerHTML;
      }
    });
  }

  static renderProductGridContainer(sectionDocument) {
    const sourceContainer = sectionDocument.getElementById("ProductGridContainer");
    const targetContainer = document.getElementById("ProductGridContainer");
    if (!sourceContainer || !targetContainer) throw new Error("Unable to render the product grid");
    targetContainer.innerHTML = sourceContainer.innerHTML;

    targetContainer.querySelectorAll(".scroll-trigger").forEach((element) => {
      element.classList.add("scroll-trigger--cancel");
    });
  }

  static renderProductCount(sectionDocument) {
    const sourceCount = sectionDocument.getElementById("ProductCount");
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    if (!sourceCount || !container) return;
    container.innerHTML = sourceCount.innerHTML;
    if (containerDesktop) containerDesktop.innerHTML = sourceCount.innerHTML;
  }

  static renderFilters(sectionDocument, event) {
    FacetFiltersForm.hydrateMobileFacetOptions(sectionDocument);
    const facetDetailsElementsFromFetch = sectionDocument.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event?.target?.closest?.(".js-filter");
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId)?.after(elementToRender.cloneNode(true));
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            ?.before(elementToRender.cloneNode(true));
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(sectionDocument);
    FacetFiltersForm.renderAdditionalElements(sectionDocument);

    if (countsToRender) {
      const closestJSFilterID = event?.target?.closest?.(".js-filter")?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        if (!newFacetDetailsElement) return;
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    document.getElementById("FacetFiltersFormMobile")?.closest("menu-drawer")?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap")?.cloneNode(true);

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  createSearchParamsFromMultipleForms(forms, changedFormParams = null) {
    const combinedParams = new URLSearchParams();

    const applyParams = (formParams) => {
      const params = new URLSearchParams(formParams);
      const keys = new Set(params.keys());
      keys.forEach((key) => combinedParams.delete(key));
      params.forEach((value, key) => combinedParams.append(key, value));
    };

    forms.forEach(applyParams);

    if (changedFormParams) applyParams(changedFormParams);

    return combinedParams.toString();
  }

  isFilterForm(form) {
    const formRole = form?.dataset?.role;
    return (
      formRole === "facet-filter-form" ||
      ["FacetFiltersForm", "FacetFiltersFormMobile"].includes(form?.id)
    );
  }

  isSortForm(form) {
    const formRole = form?.dataset?.role;
    return formRole === "facet-sort-form" || form?.id?.startsWith?.("FacetSort");
  }

  getPreferredForm(forms) {
    return forms.find((form) => form.offsetParent !== null) || forms[0];
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(this.preserveDeviceSelection(searchParams), event);
  }

  preserveDeviceSelection(searchParams) {
    const finder = document.querySelector("shop-by-device-finder");
    if (!finder) return searchParams;
    const params = new URLSearchParams(searchParams);
    const current = new URLSearchParams(window.location.search);
    [finder.dataset.filterParam, finder.dataset.familyParam, "preview_theme_id"].forEach((key) => {
      if (!key) return;
      params.delete(key);
      current.getAll(key).forEach((value) => params.append(key, value));
    });
    const theme = window.Shopify?.theme;
    if (theme && ["unpublished", "development", "demo"].includes(theme.role) && theme.id) {
      params.set("preview_theme_id", String(theme.id));
    }
    return params.toString();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    FacetFiltersForm.hydrateMobileFacetOptions(document, true);
    const changedForm = event.target?.closest?.("form");
    if (!changedForm) return;

    const allForms = Array.from(document.querySelectorAll("facet-filters-form form"));
    const filterForms = allForms.filter((form) => this.isFilterForm(form));
    const sortForms = allForms.filter((form) => this.isSortForm(form));
    const selectedFilterForm = this.isFilterForm(changedForm)
      ? changedForm
      : this.getPreferredForm(filterForms);
    const selectedSortForm = this.isSortForm(changedForm)
      ? changedForm
      : this.getPreferredForm(sortForms);
    const supportingForms = [selectedFilterForm, selectedSortForm]
      .filter(Boolean)
      .filter((form, index, forms) => form !== changedForm && forms.indexOf(form) === index)
      .map((form) => this.createSearchParams(form));
    const changedFormParams = this.createSearchParams(changedForm);

    this.onSubmitForm(
      this.createSearchParamsFromMultipleForms(supportingForms, changedFormParams),
      event
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(this.preserveDeviceSelection(url), event, true, "remove_filter");
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.requestId = 0;
FacetFiltersForm.abortController = null;
FacetFiltersForm.gridHeightState = null;
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.hydrateMobileFacetOptions();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "") minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
